/* Copyright (c) 2023-2024 Nordcloud Oy or its affiliates. All Rights Reserved. */

import {
  useApproveEventActionMutation,
  useApproveEventActionBatchMutation,
} from "~/generated/graphql";
import { showError, showSuccess } from "~/services/toast";
import { generateActionSuccessText } from "~/tools";
import { RefetchOptions, refetchQueryHelper } from "../../helpers";

type Props = {
  onSuccess: () => void;
  refetchQueries: RefetchOptions;
};

export function useApproveEvent({ onSuccess, refetchQueries }: Props) {
  const [approveEventActionMutation, { loading: actionLoading }] =
    useApproveEventActionMutation({
      onCompleted: (data) => {
        if (data.approveEventAction) {
          onSuccess();
          showSuccess(
            generateActionSuccessText("Event Action")()("approved")()
          );
        }
      },
      onError: (error) => {
        showError(error.message);
      },
      refetchQueries: [refetchQueryHelper(refetchQueries)],
    });

  const [approveEventActionBatchMutation, { loading: batchLoading }] =
    useApproveEventActionBatchMutation({
      onCompleted: (data) => {
        if (data.approveEventActionBatch) {
          onSuccess();
          showSuccess(generateActionSuccessText("Event Batch")()("approved")());
        }
      },
      onError: (error) => {
        showError(error.message);
      },
      refetchQueries: [refetchQueryHelper(refetchQueries)],
    });

  async function approveEventAction(eventId: string, actionId: string) {
    await approveEventActionMutation({
      variables: {
        eventId,
        actionId,
      },
    });
  }

  async function approveEventActionBatch(
    eventId: string,
    actionBatchId: string
  ) {
    await approveEventActionBatchMutation({
      variables: {
        eventId,
        actionBatchId,
      },
    });
  }

  return {
    approveEventAction,
    approveEventActionBatch,
    actionLoading,
    batchLoading,
  } as const;
}
